/**
 * CKAN client for data.gov.il. Replaces the legacy `ckan-api.js` with:
 *   - timeout via AbortController (not Promise.race that leaks)
 *   - explicit proxy fallback chain (no global console.warn hijack)
 *   - in-flight request dedup so concurrent searches for the same plate
 *     fire a single network request
 *
 * Returns parsed CKAN record arrays. Errors bubble up — callers decide whether
 * to retry or fall through to a fallback resource.
 */

import { Ckan } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';
import { retry } from '../../shared/promise.js';

const log = createLogger('ckan');

/** @type {Map<string, Promise<any[]>>} */
const inflight = new Map();

/**
 * Search a CKAN datastore resource by plate / filters.
 * @param {Object} args
 * @param {string} args.resourceId
 * @param {Record<string, unknown>} [args.filters]
 * @param {string} [args.q]
 * @param {number} [args.limit=50]
 * @param {string} [args.sort]   e.g. `_id desc`
 * @param {number} [args.timeoutMs=Ckan.timeoutMs]
 * @returns {Promise<any[]>}  records (empty array on no match)
 */
export function search({ resourceId, filters, q, limit = 50, sort, timeoutMs = Ckan.timeoutMs }) {
  if (!resourceId) return Promise.resolve([]);
  const url = buildUrl({ resourceId, filters, q, limit, sort });
  const key = url;

  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = retry(() => fetchJson(url, timeoutMs), {
    attempts: Ckan.proxies.length + 1, // direct + each proxy
    baseMs: 300,
    factor: 1, // we're not really backing off — each attempt uses a different transport
    shouldRetry: () => true,
  }).then(
    (j) => /** @type {any[]} */ (j?.result?.records ?? []),
    (err) => {
      log.warn('search failed for', { resourceId, filters, q }, err);
      return /** @type {any[]} */ ([]);
    },
  );

  inflight.set(key, promise);
  promise.finally(() => inflight.delete(key));
  return promise;
}

/**
 * Convenience: search a resource by exact plate (`mispar_rechev` field).
 * @param {string} resourceId
 * @param {string} plateDigits
 * @returns {Promise<any[]>}
 */
export function searchByPlate(resourceId, plateDigits) {
  if (!plateDigits) return Promise.resolve([]);
  return search({ resourceId, filters: { mispar_rechev: plateDigits } });
}

/**
 * Search the primary vehicle resource, falling back to the secondary ones if
 * no record is found. Returns the first non-empty result.
 * @param {string} plateDigits
 * @returns {Promise<{ records: any[]; resourceId: string | null }>}
 */
export async function findVehicle(plateDigits) {
  if (!plateDigits) return { records: [], resourceId: null };
  const order = [Ckan.resources.vehicles, ...Ckan.resources.vehicleFallbacks];
  for (const rid of order) {
    const records = await searchByPlate(rid, plateDigits);
    if (records.length > 0) return { records, resourceId: rid };
  }
  return { records: [], resourceId: null };
}

/**
 * Fetch ownership history records, sorted with most-recent baalut_dt first.
 * @param {string} plateDigits
 * @returns {Promise<any[]>}
 */
export async function fetchOwnershipHistory(plateDigits) {
  const records = await searchByPlate(Ckan.resources.ownership, plateDigits);
  return [...records].sort((a, b) => bestDateKey(b) - bestDateKey(a));
}

/**
 * Check whether a plate has an active disability parking tag ("תו נכה").
 *
 * The data.gov.il dataset "כלי רכב עם תג חניה לנכה" is an inclusion-only
 * list — any record for the plate means the tag is active. Note the
 * non-standard field name: `MISPAR RECHEV` is CAPITALISED with a literal
 * SPACE (not the usual `mispar_rechev` snake_case). Updated daily by the
 * Ministry of Transport.
 *
 * @param {string} plateDigits  plate digits only (8+ digits, no formatting)
 * @returns {Promise<boolean>}
 */
export async function hasDisabledTag(plateDigits) {
  if (!plateDigits) return false;
  const records = await search({
    resourceId: Ckan.resources.disabledTag,
    filters: { 'MISPAR RECHEV': plateDigits },
    limit: 1,
  });
  return records.length > 0;
}

/**
 * Fetch open manufacturer recalls that apply to a specific vehicle. Mirrors
 * the server-side `indexRecalls` + `matchesRecall` logic from
 * `functions/index.js` so the used-car-check flow can call it directly
 * (the server flow only runs for the user's own plate via the daily
 * anomaly check, not for arbitrary plates the user wants to inspect).
 *
 * Flow:
 *   1. Query the recalls dataset filtered by TOZAR_CD = vehicle.tozeret_cd
 *   2. For each result, check year range (BUILD_BEGIN_A..BUILD_END_A) and
 *      fuzzy DEGEM match against kinuy_mishari / degem_nm / sug_degem.
 *   3. Return the matched recalls (id, desc, website) for display.
 *
 * @param {any} vehicleGov   the gov record returned by `findVehicle`
 * @returns {Promise<Array<{ id: string; desc: string; web: string }>>}
 */
export async function fetchRecallsForVehicle(vehicleGov) {
  const tozar = String(vehicleGov?.tozeret_cd ?? '').trim();
  if (!tozar) return [];
  const records = await search({
    resourceId: Ckan.resources.recalls,
    filters: { TOZAR_CD: tozar },
    limit: 200,
  });
  const year = parseInt(String(vehicleGov.shnat_yitzur ?? '0'), 10);
  /** @type {string[]} */
  const candidates = [vehicleGov.kinuy_mishari, vehicleGov.degem_nm, vehicleGov.sug_degem]
    .filter(Boolean).map((s) => String(s).toUpperCase());

  /** @type {Array<{ id: string; desc: string; web: string }>} */
  const matched = [];
  for (const r of records) {
    const from = parseInt(String(r.BUILD_BEGIN_A ?? '').slice(0, 4), 10) || 0;
    const to   = parseInt(String(r.BUILD_END_A   ?? '').slice(0, 4), 10) || 9999;
    if (year && (year < from || year > to)) continue;

    const degem = String(r.DEGEM ?? '').trim().toUpperCase();
    if (degem && !candidates.some((c) => c.includes(degem) || degem.includes(c))) continue;

    matched.push({
      id:   String(r.RECALL_ID ?? r._id ?? ''),
      desc: String(r.TEUR_TAKALA ?? r.SUG_TAKALA ?? 'קריאת בטיחות').trim(),
      web:  String(r.WEBSITE ?? '').trim(),
    });
  }
  return matched;
}

// ---------- internals ----------

/**
 * @param {Object} args
 * @param {string} args.resourceId
 * @param {Record<string, unknown>} [args.filters]
 * @param {string} [args.q]
 * @param {number} [args.limit]
 * @param {string} [args.sort]
 */
function buildUrl({ resourceId, filters, q, limit, sort }) {
  const u = new URL(Ckan.baseUrl);
  u.searchParams.set('resource_id', resourceId);
  if (filters) u.searchParams.set('filters', JSON.stringify(filters));
  if (q) u.searchParams.set('q', String(q));
  if (limit != null) u.searchParams.set('limit', String(limit));
  if (sort) u.searchParams.set('sort', String(sort));
  return u.toString();
}

/**
 * Fetch JSON, racing direct + every configured CORS proxy in parallel. Each
 * transport gets its own AbortController so a slow one can't poison the
 * others — the previous design shared one controller, and once the global
 * timeout fired the proxies were never tried because their fetches inherited
 * an already-aborted signal. Now whichever transport responds first wins.
 *
 * @param {string} url
 * @param {number} timeoutMs
 */
async function fetchJson(url, timeoutMs) {
  const transports = [url, ...Ckan.proxies.map((p) => p + encodeURIComponent(url))];
  return Promise.any(transports.map((t) => fetchOne(t, timeoutMs)));
}

/**
 * @param {string} transport
 * @param {number} timeoutMs
 */
async function fetchOne(transport, timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(transport, {
      cache: 'no-store',
      signal: ctrl.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Score a CKAN ownership record by its best-available date field. Used to
 * sort history newest-first. Returns -1 when no readable date is found.
 * @param {any} rec
 */
function bestDateKey(rec) {
  const candidates = ['baalut_dt', 'mivchan_acharon_dt', 'tokef_dt'];
  for (const field of candidates) {
    const raw = rec?.[field];
    if (raw == null) continue;
    const digits = String(raw).replace(/\D/g, '');
    if (digits.length >= 6) return Number(digits.slice(0, 8));
  }
  return -1;
}
